// Importing React and related libs
import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

// Importing other external libs
import * as d3 from 'd3';
import * as fc from 'd3fc';

// Importing component helpers
// Importing local components & hooks
import useGenomePositions from 'helix-hooks/chart/useGenomePositions';
import { getAdjustedPointSize } from 'helix/chart/hepler';
import { DISEASE_REGION_COLOR, FLANKING_COLOR } from 'helix-styles/colors';

import './style.scss';

const POINT_COLORS = {
  'NC': [252 / 255, 152 / 255, 53 / 255, 1],
  'AA': [0 / 255, 114 / 255, 178 / 255, 1],
  'AB': [0 / 255, 114 / 255, 178 / 255, 1],
  'BB': [0 / 255, 114 / 255, 178 / 255, 1]
};

const getFlankingRegions = ({ flanking, flankingColor, regions }) => {
  const flankingRegions = [];
  for (const region of regions) {
    flankingRegions.push(
      { start: region.start - (flanking * 1e6), end: region.start, ...flankingColor },
      { start: region.end, end: region.end + (flanking * 1e6), ...flankingColor }
    );
  }
  return flankingRegions;
};

const getChromPositionFromGenomicPosition = ({ chromPositions, genomicPosition }) => {
  if (Object.values(chromPositions).length === 0) return genomicPosition;

  const [chrom, position] = Object.entries(chromPositions).find(
    ([, positions]) => positions.genomeStart <= genomicPosition && positions.genomeEnd >= genomicPosition
  ) ?? [];

  return `${chrom}:${genomicPosition - (position?.genomeStart ?? 0)}`;
};

const GenomePlot = ({
  assembly = 'hg38',
  data,
  diseaseRegions,
  diseaseRegionsColor,
  diseaseRegionsOpacity,
  diseaseRegionsPositioning,
  flanking,
  flankingColor,
  gradientColors,
  height,
  id,
  mutationSiteColor,
  mutationSiteWidth,
  pointSize,
  useDevicePixelRatio,
  xExtent,
  xKey,
  yExtent,
  yKey,
}) => {
  const adjustedPointSize = getAdjustedPointSize(pointSize, useDevicePixelRatio);
  const chartRef = useRef();
  const geneRegionsRef = useRef();

  const {
    chroms,
    chromPositions,
    genomeExtent,
    isLoading,
  } = useGenomePositions(assembly);

  const finalExtent = Array.isArray(xExtent) && xExtent.length === 2 ? xExtent : genomeExtent;

  const finalRegions = useMemo(
    () => diseaseRegionsPositioning === 'chrom' && !isLoading ? diseaseRegions.map(region => {
      const { genomeStart } = chromPositions[region.chrom];
      return {
        ...region,
        start: genomeStart + region.start,
        end: genomeStart + region.end,
        mutationSite: genomeStart + region.mutationSite,
      };
    }) : diseaseRegions,
    [isLoading, diseaseRegionsPositioning]
  );

  const dataWithColor = useMemo(
    () => data.map(point => ({ ...point, color: POINT_COLORS[point.call] })),
    [data]
  );

  useEffect(() => {
    if (!chartRef.current) return;

    const { clientHeight, clientWidth } = chartRef.current.querySelector(
      '.plot-area'
    ) ?? { clientWidth: 100, clientHeight: 100 };

    if (clientWidth < 100 && clientHeight < 100) return;

    const xScale = d3.scaleLinear().domain(finalExtent).range([0, clientWidth]);
    const yScale = d3.scaleLinear().domain(yExtent);

    const chromCenterPositions = Object.values(chromPositions).map(pos => pos.genomeCenter);
    const [viewStart, viewEnd] = finalExtent;
    const visibleChroms = Object.values(chromPositions).filter(
      positions => positions.genomeStart >= viewStart && positions.genomeEnd <= viewEnd
    ).length;

    const xAxisChromLabels = (scale) => fc.axisBottom(scale)
      .tickValues(Object.values(chromCenterPositions))
      .tickFormat(d3.scaleOrdinal().domain(chromCenterPositions).range(chroms));

    const xAxisChromPositions = (scale) => fc.axisBottom(scale).tickFormat(
      (genomicPosition) => getChromPositionFromGenomicPosition({ chromPositions, genomicPosition, })
    );

    const chart = fc.chartCartesian({
      xScale,
      yScale,
      xAxis: {
        bottom: visibleChroms < 3 ? xAxisChromPositions : xAxisChromLabels
      }
    }).useDevicePixelRatio(useDevicePixelRatio);

    const pointSeries = fc
      .seriesWebglPoint()
      .crossValue((dataPoint) => dataPoint[xKey])
      .mainValue((dataPoint) => dataPoint[yKey]).size(adjustedPointSize)
      .decorate((program) => {
        fc
          .webglFillColor()
          .value((dataPoint) => dataPoint.color)
          .data(dataWithColor)(program);
      });

    chart.webglPlotArea(pointSeries);

    d3.select(chartRef.current).datum(dataWithColor).call(chart);

    d3.select(chartRef.current).select('d3fc-group')
      .selectAll('canvas.gene-regions')
      .data([1])
      .join(
        enter => enter.insert('canvas').lower()
      )
      .attr('class', 'gene-regions')
      .attr('width', clientWidth)
      .attr('height', clientHeight);

    const canvas = chartRef.current.querySelector('canvas.gene-regions');
    const ctx = canvas.getContext('2d');

    [
      ...getFlankingRegions({ flanking, flankingColor, regions: finalRegions }),
      ...finalRegions
    ].forEach(region => {
      const regionWidth = xScale(region.end) - xScale(region.start);
      const regionStrokeWidth = 2;

      ctx.fillStyle = region.fill ?? diseaseRegionsColor.fill;
      ctx.fillRect(xScale(region.start), 0, xScale(region.end) - xScale(region.start), clientHeight);

      const drawVerticalLine = (pos) => {
        ctx.beginPath();
        ctx.moveTo(xScale(pos), 0);
        ctx.lineTo(xScale(pos), clientHeight);
        ctx.stroke();
      };

      [region.start, region.end].forEach(pos => {
        ctx.lineWidth = regionStrokeWidth;
        ctx.strokeStyle = region.stroke ?? diseaseRegionsColor.stroke;
        drawVerticalLine(pos);
      });

      if (region.mutationSite && mutationSiteWidth + regionStrokeWidth < regionWidth) {
        ctx.lineWidth = mutationSiteWidth;
        ctx.strokeStyle = mutationSiteColor;
        drawVerticalLine(region.mutationSite);
      }
    });
  }, [chartRef, dataWithColor, finalRegions, diseaseRegionsColor, diseaseRegionsOpacity, geneRegionsRef, finalExtent, isLoading]);

  const linearGradient = Object.values(chromPositions).map(({ genomeStart, genomeEnd }, i) => {
    const visibleLength = finalExtent[1] - finalExtent[0];
    // account for zooming in / out
    const start = (genomeStart - finalExtent[0]) / visibleLength;
    const end = (genomeEnd - finalExtent[0]) / visibleLength;

    const color = gradientColors[i % gradientColors.length];
    const startPercentage = start * 100;
    const endPercentage = end * 100;

    if (endPercentage < 0 || startPercentage > 100) return '';

    return `${color} ${startPercentage}%, ${color} ${endPercentage}%`;
  });
  const linearGradientCSS = linearGradient.filter(percentages => !!percentages).join(', ');
  const background = `linear-gradient(to right, ${linearGradientCSS})`;

  return (
    <div
      className='genome-chart'
      id={id}
      ref={chartRef}
      style={{
        width: '100%',
        height,
        '--canvasBackground': background
      }}
    />
  );
};

const REGION_STYLE_SHAPE = PropTypes.shape({
  fill: PropTypes.string.isRequired,
  stroke: PropTypes.string.isRequired,
});

GenomePlot.propTypes = {
  assembly: PropTypes.oneOf(['hg19', 'hg38']).isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      genomicPosition: PropTypes.number.isRequired,
    }).isRequired
  ),
  diseaseRegions: PropTypes.arrayOf(PropTypes.shape({
    start: PropTypes.number.isRequired,
    end: PropTypes.number.isRequired,
    mutationSite: PropTypes.number
  })),
  diseaseRegionsColor: REGION_STYLE_SHAPE,
  diseaseRegionsPositioning: PropTypes.oneOf(['chrom', 'genome']),
  flankingColors: REGION_STYLE_SHAPE,
  gradientColors: PropTypes.arrayOf(PropTypes.string).isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  id: PropTypes.string,
  mutationSiteColor: PropTypes.string,
  mutationSiteWidth: PropTypes.number,
  pointSize: PropTypes.number.isRequired,
  useDevicePixelRatio: PropTypes.bool.isRequired,
  xExtent: PropTypes.arrayOf(PropTypes.number),
  xKey: PropTypes.string.isRequired,
  yExtent: PropTypes.arrayOf(PropTypes.number).isRequired,
  yKey: PropTypes.string.isRequired,
};

GenomePlot.defaultProps = {
  assembly: 'hg38',
  data: [],
  diseaseRegions: [],
  diseaseRegionsColor: DISEASE_REGION_COLOR,
  diseaseRegionsPositioning: 'chrom',
  flanking: 2,
  flankingColor: FLANKING_COLOR,
  gradientColors: ['#f3f3f3', '#ffffff'],
  height: 320,
  mutationSiteColor: '#000000',
  mutationSiteWidth: 2,
  pointSize: 2.5,
  useDevicePixelRatio: true,
  xKey: 'genomicPosition',
  yExtent: [-2, 2],
  yKey: 'logr',
};

export default GenomePlot;